import { useState } from 'react';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Mail, Send, CheckCircle, XCircle } from 'lucide-react';
import { useEmailConfig, useUpdateEmailConfig, useSendTestEmail, useEmailLog } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import type { EmailEventType } from '@/types/models';

const EVENT_LABELS: Record<EmailEventType, string> = {
  work_order_created: 'Work Order Created',
  intake_complete: 'Intake Complete',
  processing_complete: 'Processing Complete',
  shipment_created: 'Shipment Created',
  destruction_certificate: 'Destruction Certificate',
};

export default function EmailConfigPage() {
  const { data: config } = useEmailConfig();
  const { data: logs = [] } = useEmailLog();
  const updateConfig = useUpdateEmailConfig();
  const sendTest = useSendTestEmail();

  const [senderName, setSenderName] = useState('');
  const [replyTo, setReplyTo] = useState('');

  const initialized = config && !senderName && !replyTo;
  if (initialized) {
    setSenderName(config.sender_name);
    setReplyTo(config.reply_to_email);
  }

  const handleSave = () => {
    updateConfig.mutate({ sender_name: senderName, reply_to_email: replyTo }, {
      onSuccess: () => toast.success('Email settings saved'),
    });
  };

  const handleToggle = (event: EmailEventType) => {
    if (!config) return;
    const updated = { ...config.notifications, [event]: !config.notifications[event] };
    updateConfig.mutate({ notifications: updated });
  };

  return (
    <>
      <PageHeader title="Email Configuration" description="Manage email notification settings and view send history" />

      <div className="mt-6 space-y-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Sender Settings</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Sender Name</Label>
                <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Company Name" />
              </div>
              <div className="space-y-2">
                <Label>Reply-To Email</Label>
                <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="support@company.com" type="email" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={updateConfig.isPending}>Save Settings</Button>
              <Button variant="outline" onClick={() => sendTest.mutate(undefined, { onSuccess: () => toast.success('Test email sent!') })} disabled={sendTest.isPending}>
                <Send className="mr-2 h-4 w-4" /> Send Test Email
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Notification Toggles</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(EVENT_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between py-2">
                <Label className="text-sm">{label}</Label>
                <Switch checked={config?.notifications[key as EmailEventType] ?? false} onCheckedChange={() => handleToggle(key as EmailEventType)} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Email Send Log</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">{log.recipient}</TableCell>
                    <TableCell className="text-sm">{log.subject}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{EVENT_LABELS[log.event_type]}</Badge></TableCell>
                    <TableCell>
                      {log.status === 'sent' ? (
                        <span className="flex items-center gap-1 text-sm text-status-complete"><CheckCircle className="h-3.5 w-3.5" /> Sent</span>
                      ) : (
                        <span className="flex items-center gap-1 text-sm text-destructive"><XCircle className="h-3.5 w-3.5" /> {log.status}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(log.sent_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
